import type { FormField } from './form';

export interface NetworkStudy {
  title: string;
  uri: string;
  longitudinal: boolean;
  org: string;
  b64Identifier: string;
  avatar: string;
  creation: string;
  archivedAt: string;
  flags?: StudyFlags;
}

export interface StudyFlags {
  spRedirect: boolean;
  importExportStudy: boolean;
  customDataDictionary?: string;
  preferences?: StudyPreferences;
  entryCompletionLogic?: EntryCompletionLogic;
  disableInteractiveMode?: boolean;
  cfrPart11?: boolean;
  appVersion?: string;
  entrySaveButtonText?: string;
  allowDuplicateCaseNames?: boolean;
  showSaveEntryButtonAtBottom?: boolean;
  enableEntryMetrics?: boolean;
  enableStudyLinkManagement?: boolean;
  caseNameLogic?: CaseNameLogic;
  cachePublicEntry?: boolean;
  pumpQcReports?: PumpQcReports;
  // Custom message shown when a form has data entry closed. Falls back to a default when empty.
  closedFormMessage?: string;
}

interface PumpQcReports {
  enable: boolean;
}

interface CaseNameLogic {
  incremental: {
    prefix: string;
    defaultStart?: number;
  };
  suffix: {
    formId: string;
    fieldId: string;
    connector: string;
  };
}

interface StudyPreferences {
  dataViewFilter?: string;
}

interface EntryCompletionLogic {
  requirementEffect: string[]
}

interface IStudy {
  identifier: string;
  b64Identifier: string;
  title: string;
  longitudinal: boolean;
  organizationIdentifier: string;
  avatar: string;
  creation: string;
  archivedAt: string;

  flags?: StudyFlags;
}

export interface INewStudy {
  long: string;
  organizationIdentifier?: string;
  title: string;
}

export interface StudySchemaExport {
  formIdentifiers?: string[];
  flatHumanReadableOptions?: boolean;
}

export interface StudyChanges {
  identifier?: string;
  title?: string;
  description?: string;
  isCaseManagement?: boolean;
  entryCreatedWebhook?: string;
  entryUpdatedWebhook?: string;
  enforcements?: string;
}

interface StudyConfigChanges {
  previous: StudyChanges;
  changes: StudyChanges;
  errors: StudyChanges;
}

interface FieldUpdates {
  fieldsDeleted: number;
  fieldsAdded: number;
  fieldsChanged: number;
}

export interface FormChanges {
  previous: StudyChanges;
  changes: StudyChanges;
  errors: StudyChanges;
  fieldUpdates: FieldUpdates;
}

export interface FormFieldMoved {
  current?: number;
  previous?: number;
}

export interface FormFieldChanges {
  newField?: boolean;
  moved?: FormFieldMoved,
  deleted?: boolean;
}

export interface FormConfig {
  formChanges: FormChanges;
  items: FormField[];
}

export interface StudyImportDiff {
  studyConfigChanges: StudyConfigChanges;
  forms: FormConfig[];
}

export class StudyModel {
  identifier: string;
  b64Identifier: string;
  title: string;

  //longitudinal true means case management ON
  longitudinal: boolean;
  organizationIdentifier: string;
  avatar: string;
  creation: string;
  archivedAt: string;

  flags?: StudyFlags;

  constructor({
    identifier,
    b64Identifier,
    title,
    longitudinal,
    organizationIdentifier,
    avatar,
    creation,
    archivedAt,
    flags
  }: IStudy) {
    this.identifier = identifier;
    this.b64Identifier = b64Identifier;
    this.title = title;
    this.longitudinal = longitudinal;
    this.organizationIdentifier = organizationIdentifier;
    this.avatar = avatar;
    this.creation = creation;
    this.archivedAt = archivedAt;
    this.flags = flags;
  }

  static mapFromBackend(networkStudy: NetworkStudy) {
    return new StudyModel({
      identifier: networkStudy.uri.split('/')[1],
      b64Identifier: networkStudy.b64Identifier,
      title: networkStudy.title,
      longitudinal: networkStudy.longitudinal,
      organizationIdentifier: networkStudy.org,
      avatar: networkStudy.avatar,
      creation: networkStudy.creation,
      archivedAt: networkStudy.archivedAt,
      flags: networkStudy.flags
    });
  }

  static createEmpty() {
    return new StudyModel({
      identifier: '',
      b64Identifier: '',
      title: '',
      longitudinal: true,
      organizationIdentifier: '',
      avatar: '',
      creation: '',
      archivedAt: ''
    });
  }
}
